import { useEffect } from 'react';

/**
 * Per-page SEO + social meta + structured data for our client-rendered SPA.
 *
 * On mount it sets the document title, description, canonical, Open Graph /
 * Twitter tags, an optional social image, and injects JSON-LD (a FAQPage built
 * from `faq`, plus any objects passed in `jsonLd`). On unmount it restores the
 * previous meta values and removes the JSON-LD it added, so nothing leaks across
 * client-side navigations. Crawlers see all of this because the build-time
 * prerender (frontend/scripts/prerender.js) renders each public route after
 * JavaScript has run and saves the result as static HTML.
 *
 * Args:
 *   title, description, canonical    - core meta (canonical also sets og:url)
 *   ogTitle, ogDescription           - optional, fall back to title/description
 *   image, imageAlt                  - optional absolute URL for og:image/twitter:image
 *   faq      - optional [{ question, answer }] -> rendered as FAQPage JSON-LD
 *   jsonLd   - optional array of plain schema.org objects (Service, BreadcrumbList, ...)
 *
 * Pass `faq` / `jsonLd` as stable references (module constant or useMemo) so the
 * effect does not re-run on every render.
 */
export default function useSeo({
  title,
  description,
  canonical,
  // Every page that calls useSeo is a public marketing page, so this defaults
  // to indexable. It matters because build/app.html — the document served for
  // anything without its own prerendered file — is marked noindex, to stop
  // URLs that do not exist inviting crawlers in. A real page rendering from
  // that fallback would otherwise inherit noindex and quietly disappear.
  // Pages that are genuinely missing use useCrawlerNotFound instead.
  robots = 'index, follow, max-image-preview:large',
  ogTitle,
  ogDescription,
  image,
  imageAlt,
  faq,
  jsonLd,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const touched = [];
    const setMeta = (matchAttr, matchVal, value) => {
      if (value == null) return;
      let el = document.head.querySelector(`meta[${matchAttr}="${matchVal}"]`);
      let created = false;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(matchAttr, matchVal);
        document.head.appendChild(el);
        created = true;
      }
      touched.push({ el, prev: el.getAttribute('content'), created });
      el.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    // A page that knows it is public says so. The SPA fallback document
    // (build/app.html) carries noindex, because it is served both for real
    // application routes and for URLs that do not exist — so a public page
    // rendering from it, rather than from its own prerendered file, has to
    // set the record straight once React runs.
    setMeta('name', 'robots', robots);
    setMeta('property', 'og:title', ogTitle || title);
    setMeta('property', 'og:description', ogDescription || description);
    setMeta('name', 'twitter:title', ogTitle || title);
    setMeta('name', 'twitter:description', ogDescription || description);
    if (canonical) setMeta('property', 'og:url', canonical);
    if (image) {
      setMeta('property', 'og:image', image);
      setMeta('name', 'twitter:image', image);
      setMeta('name', 'twitter:card', 'summary_large_image');
      if (imageAlt) setMeta('property', 'og:image:alt', imageAlt);
    }

    const canon = document.querySelector('link[rel="canonical"]');
    const prevCanon = canon ? canon.getAttribute('href') : null;
    if (canon && canonical) canon.setAttribute('href', canonical);

    // Build and inject JSON-LD blocks.
    const blocks = [];
    if (faq && faq.length) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      });
    }
    if (jsonLd && jsonLd.length) {
      jsonLd.forEach((b) => b && blocks.push(b));
    }
    const scripts = blocks.map((b) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-hook', 'true');
      s.text = JSON.stringify(b);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      if (canon && prevCanon) canon.setAttribute('href', prevCanon);
      touched.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute('content', prev);
      });
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, robots, ogTitle, ogDescription, image, imageAlt, faq, jsonLd]);
}
